import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, EmptyState, Loading, SectionTitle, scoreTone, fmtDate } from "@/components/common";
import { GradeSelect } from "@/pages/AuthPage";
import { CourseCreateDialog } from "@/pages/CreatePage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, School, ClipboardList, Library, Plus, Copy, Sparkles, Loader2, FolderPlus } from "lucide-react";
import { toast } from "sonner";

export const ClassCreateDialog = ({ open, onOpenChange, onCreated, label = "New class" }) => {
  const [name, setName] = useState("");
  const [grade, setGrade] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) return toast.error("Give your class a name");
    setBusy(true);
    try {
      const { data } = await api.post("/classes", { name, grade_band: grade || null });
      toast.success(`Class created — join code ${data.join_code}`);
      setName("");
      setGrade("");
      onCreated?.(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="class-create-dialog">
        <DialogHeader><DialogTitle className="font-display">{label}</DialogTitle><DialogDescription>Students join with a code you share. You can then assign lessons and see their scores.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Class name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 4th Grade Math — Period 2" className="h-11" data-testid="class-name-input" /></div>
          <div className="space-y-2"><Label>Grade level (optional)</Label><GradeSelect value={grade} onChange={setGrade} testid="class-grade-select" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="class-create-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create class</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ClassCard = ({ c }) => (
  <Link to={`/classes/${c.class_id}`} className="bg-white border border-border rounded-[14px] p-5 lift block" data-testid="class-card">
    <div className="font-display font-semibold text-lg">{c.name}</div>
    <div className="text-xs text-muted-foreground mt-1">{c.grade_band || "Mixed grades"} · {c.student_count} students</div>
    <div className="mt-4 flex items-center justify-between bg-[#FBFAF4] border border-border rounded-lg px-3 py-2">
      <div>
        <div className="text-[11px] text-muted-foreground">Join code</div>
        <div className="font-display font-semibold tracking-[0.2em]" data-testid="teacher-class-join-code">{c.join_code}</div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Copy join code"
        onClick={(e) => { e.preventDefault(); navigator.clipboard?.writeText(c.join_code); toast.success("Join code copied"); }}
        data-testid="copy-join-code-button"
      >
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  </Link>
);

export default function TeacherDashboard() {
  const { user, refresh, refreshBilling } = useAuth();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [schoolCode, setSchoolCode] = useState(params.get("school") || "");
  const joinSchool = async (e) => {
    e.preventDefault();
    if (!schoolCode.trim()) return;
    try {
      const { data } = await api.post("/school/join", { code: schoolCode });
      toast.success(`You joined ${data.school_name}`);
      setSchoolCode("");
      await refresh();
      refreshBilling();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const leaveSchool = async () => {
    try { await api.post("/school/leave"); toast.success("You left the school"); await refresh(); refreshBilling(); } catch (err) { toast.error(errMsg(err)); }
  };
  const [courseOpen, setCourseOpen] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/teacher/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loading />;

  return (
    <div>
      <PageHeader
        eyebrow="Teacher"
        title={`Welcome, ${user.name.split(" ")[0]}`}
        subtitle="Run your classes, assign lessons, and let Sage handle one-on-one tutoring."
        actions={
          <>
            <Button variant="outline" className="bg-white" onClick={() => setCourseOpen(true)} data-testid="teacher-new-course-button"><FolderPlus className="h-4 w-4 mr-2" />New course</Button>
            <Button variant="outline" className="bg-white" asChild data-testid="teacher-ai-lesson-button"><Link to="/create"><Sparkles className="h-4 w-4 mr-2" />AI lesson</Link></Button>
            <Button onClick={() => setOpen(true)} data-testid="teacher-new-class-button"><Plus className="h-4 w-4 mr-2" />New class</Button>
          </>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={School} label="Classes" value={d.classes.length} testid="teacher-stat-classes" />
        <StatCard icon={Users} label="Students" value={d.student_count} tone="sky" testid="teacher-stat-students" />
        <StatCard icon={ClipboardList} label="Assignments" value={d.assignment_count} tone="amber" testid="teacher-stat-assignments" />
        <StatCard icon={Library} label="My lessons" value={d.lesson_count} hint={`${d.course_count} courses`} tone="green" testid="teacher-stat-lessons" />
      </div>

      <div className="bg-white border border-border rounded-[14px] p-5 mb-8 flex flex-col md:flex-row md:items-center gap-4" data-testid="teacher-school-card">
        {user.school_id ? (
          <>
            <div className="flex-1"><div className="font-semibold">School: {user.school_name}</div><div className="text-sm text-muted-foreground">Your students are covered by your school's seats.</div></div>
            <Button variant="ghost" onClick={leaveSchool} data-testid="teacher-leave-school-button">Leave school</Button>
          </>
        ) : (
          <>
            <div className="flex-1"><div className="font-semibold">Part of a school?</div><div className="text-sm text-muted-foreground">Enter your school's code to join for free and share its student seats.</div></div>
            <form onSubmit={joinSchool} className="flex gap-2 md:w-[360px]">
              <Input value={schoolCode} onChange={(e) => setSchoolCode(e.target.value.toUpperCase())} placeholder="School code" className="h-11 uppercase" data-testid="teacher-school-code-input" />
              <Button type="submit" className="h-11" data-testid="teacher-join-school-button">Join</Button>
            </form>
          </>
        )}
      </div>

      <SectionTitle>Your classes</SectionTitle>
      {d.classes.length === 0 ? (
        <EmptyState icon={School} title="Create your first class" text="You'll get a join code to share with students." action={<Button onClick={() => setOpen(true)} data-testid="teacher-empty-new-class">New class</Button>} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="teacher-classes-list">
          {d.classes.map((c) => <ClassCard key={c.class_id} c={c} />)}
        </div>
      )}

      <div className="mt-10">
        <SectionTitle>Latest quiz results</SectionTitle>
        <div className="bg-white border border-border rounded-[14px] overflow-hidden">
          {d.recent_attempts.length === 0 ? (
            <p className="text-sm text-muted-foreground p-5">Student quiz results will appear here.</p>
          ) : (
            <Table data-testid="teacher-recent-attempts-table">
              <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Lesson</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Score</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.recent_attempts.map((a) => (
                  <TableRow key={a.attempt_id}>
                    <TableCell className="font-medium">{a.student_name}</TableCell>
                    <TableCell>{a.lesson_title}</TableCell>
                    <TableCell className="text-muted-foreground">{fmtDate(a.created_at)}</TableCell>
                    <TableCell className={`text-right font-display font-semibold ${scoreTone(a.percent)}`}>{a.percent}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
      <ClassCreateDialog open={open} onOpenChange={setOpen} onCreated={(c) => { setOpen(false); navigate(`/classes/${c.class_id}`); }} />
      <CourseCreateDialog open={courseOpen} onOpenChange={setCourseOpen} onCreated={(c) => { setCourseOpen(false); navigate(`/courses/${c.course_id}`); }} />
    </div>
  );
}
